/* global window, document */

import get from 'lodash/get';
import UAParser from 'ua-parser-js';
import isMobileJS from 'ismobilejs';

import { generateRandomId } from './utils';

/**
 * Class representing an ad tag.
 */
class AdTag {
  /**
   * Create an ad tag.
   * @param {Object} player - videojs player.
   * @param {Object} source - video embed.
   * @param {Object} bid - header bid.
   * @param {Object} settings - settings.
   */
  constructor(player, source, bid, settings) {
    this.player = player;
    this.source = source;
    this.bid = bid;
    this.settings = settings;
  }

  /**
   * Get referrer domain.
   */
  getRefDomain() {
    let refdomain = '';
    const networks = [
      {
        key: 'social_facebook_inapp',
        ua: 'FB',
      },
      {
        key: 'social_facebook',
        ref: 'facebook\\.com',
        query: 'spr_fb',
      },
      {
        key: 'social_google',
        ref: '(plus|plus\\.url)\\.google\\.com',
        ua: 'GooglePlus',
      },
      {
        key: 'social_pinterest',
        ref: 'pinterest\\.com',
        ua: 'Pinterest',
      },
      {
        key: 'social_twitter_ios_app',
        ua: 'Twitter for iPhone',
      },
      {
        key: 'social_twitter',
        ref: 't\\.co',
        query: 'spr_tw',
        ua: 'Twitter',
      },
    ];

    for (const network of networks) {
      // Check user agent
      if (get(network, 'ua')) {
        const regUA = new RegExp(network.ua, 'i');
        if (window.navigator.userAgent.match(regUA)) {
          refdomain = network.key;
          break;
        }
      }

      // Check referring domain
      if (get(network, 'ref')) {
        const regRef = new RegExp(network.ref, 'i');
        if (document.referrer.match(regRef)) {
          refdomain = network.key;
          break;
        }
      }

      // Check query parameters
      if (get(network, 'query')) {
        const regQuery = new RegExp(network.query, 'i');
        if (window.location.search.match(regQuery)) {
          refdomain = network.key;
          break;
        }
      }
    }

    return refdomain;
  }

  /**
   * Get if browser is not NHTSafe
   */
  isBrowserNotNHTSafe(prefix, safeVersion) {
    try {
      const parser = new UAParser();
      const result = parser.getResult();
      if (result.browser.name.toLowerCase().startsWith(prefix)) {
        const version = parseInt(result.browser.version.split('.')[0], 10);
        if (version < safeVersion) {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get OS and Browser in the format <OS>-<OS version>_<browser>-<browser version>.
   */
  getOSAndBrowser() {
    const parser = new UAParser();
    const result = parser.getResult();

    const osb = `${result.browser.name}-${result.browser.version}_${result.os.name}-${result.os.version}`;

    return osb.toLowerCase().replace(/ /g, '');
  }

  /**
   * Get Ad request ID.
   */
  getAdRequestId() {
    return generateRandomId();
  }

  /**
   * Get additional KV params.
   */
  getAdditionalKVParams() {
    const additionalKVs = [];

    if (get(window, 'HRST.article.section.prefix')) {
      additionalKVs.push(`sect=${window.HRST.article.section.prefix.replace(/-/g, '_')}`);
    }

    if (get(window, 'HRST.article.subSection.prefix')) {
      additionalKVs.push(`sub=${window.HRST.article.subSection.prefix.replace(/-/g, '_')}`);
    }

    if (get(window, 'HRST.article.collections')) {
      additionalKVs.push(`clc=${window.HRST.article.collections}`);
    }

    return additionalKVs.length > 0 ? additionalKVs.join('&') : '';
  }

  /**
   * Get source Ad slot.
   */
  getAdSlot() {
    if (this.source.index > 0) {
      return 'recommended';
    }

    if (this.source.isPlaylist || this.source.adStartEvent !== 'inViewPlay') {
      return `${1}`;
    }

    window.hearstPlayer.incAutoplayCount();

    return window.hearstPlayer.autoplayCount;
  }

  /**
   * Get custom params.
   */
  getCustomParams() {
    const customParams = [];

    const additionalKVParams = this.getAdditionalKVParams();
    if (additionalKVParams.length) {
      customParams.push(additionalKVParams);
    }

    customParams.push('tool=video');
    customParams.push('pos=8');

    if (get(window, 'HRST.article.universalID')) {
      customParams.push(`artid=${window.HRST.article.universalID}`);
    }

    customParams.push(`adslot=${this.getAdSlot()}`);

    customParams.push(`refdomain=${this.getRefDomain()}`);

    if (get(window, 'HRST.article.pageUrl')) {
      customParams.push(`pageUrl=${window.HRST.article.pageUrl}`);
    }

    if (get(window, 'HRST.site.name')) {
      customParams.push(`site=${window.HRST.site.name}`);
    }

    if (get(window, 'dataMapping.urlHash')) {
      customParams.push(`urlHash=${window.dataMapping.urlHash}`);
    }

    if (get(window, 'LUMIERE_CUSTOM_TOKEN')) {
      customParams.push(`token=${window.LUMIERE_CUSTOM_TOKEN}`);
    }

    if (get(window, 'LUMIERE_PLATFORM')) {
      customParams.push(`pltfrm=${window.LUMIERE_PLATFORM}`);
    }

    customParams.push(`videoID=${get(this.source, 'attributes.media.id')}`);
    customParams.push(`embedID=${get(this.source, 'id')}`);

    if (this.player.currentWidth() > 0 && this.player.currentHeight() > 0) {
      customParams.push(`width=${Math.floor(this.player.currentWidth())}`);
      customParams.push(`height=${Math.floor(this.player.currentHeight())}`);
    }

    if (this.isBrowserNotNHTSafe('chrom', 75) || this.isBrowserNotNHTSafe('safari', 12) || this.isBrowserNotNHTSafe('firefox', 78)) {
      customParams.push('nhtsafe=false');
    } else {
      customParams.push('nhtsafe=true');
    }

    customParams.push(`vid_t=${get(this.source, 'title')}`);

    customParams.push(`osb=${this.getOSAndBrowser()}`);

    customParams.push(`ad_request_id=${this.getAdRequestId()}`);

    customParams.push(`dp=${this.settings.doNotSellStatus ? 1 : 0}`);

    return customParams.length > 0 ? customParams.join('&') : '';
  }

  /**
   * Get player dimensions.
   */
  getPlayerDimensions() {
    return '640x480';
  }

  /**
   * Get user connection speed.
   */
  getUserConnectionSpeed() {
    let bitrate = 'mobilebitrate';

    if (window.navigator.connection && window.navigator.connection.downlink) {
      const downlink = Math.abs(window.navigator.connection.downlink * 1000);
      if (downlink <= 300) {
        bitrate = 'mobilebitrate';
      } else if (downlink <= 600) {
        bitrate = 'verylowbitrate';
      } else if (downlink <= 1000) {
        bitrate = 'lowbitrate';
      } else if (downlink <= 5000) {
        bitrate = 'medbitrate';
      } else {
        bitrate = 'highbitrate';
      }

      return bitrate;
    }

    if (isMobileJS.phone) {
      bitrate = 'mobilebitrate';
    } else if (isMobileJS.tablet) {
      bitrate = 'verylowbitrate';
    } else {
      bitrate = 'lowbitrate';
    }

    return bitrate;
  }

  /**
   * Return ad tag URL.
   */
  get url() {
    const dartZone = get(window, 'HRST.site.dartZone');
    const adUnit = get(window, 'HRST.site.adUnit');

    if (!dartZone || !adUnit) {
      return null;
    }

    const params = {
      gdfp_req: 1,
      env: 'vp',
      impl: 's',
      unviewed_position_start: 1,
      output: 'xml_vast3',
      sz: `${this.getPlayerDimensions()}|3x3`,
      iu: `/${dartZone}/${adUnit}`,
      url: encodeURIComponent(get(window, 'HRST.article.pageUrl', window.location.href)),
      ciu_szs: isMobileJS.phone ? '320x50,300x250' : '300x250,728x90',
      correlator: Date.now(),
      description_url: encodeURIComponent(get(window, 'HRST.article.pageUrl', window.location.href)),
      cust_params: encodeURIComponent(this.getCustomParams()),
      vpos: 'preroll',
      pp: this.getUserConnectionSpeed(),
    };

    const { scp } = this.bid;
    if (scp.length > 0) {
      params.scp = scp;
    }

    if (this.settings.doNotSellStatus) {
      params.rdp = 1;
    }

    const paramsString = Object.keys(params).map(key => [key, params[key]].join('=')).join('&');

    const adTagUrl = `https://pubads.g.doubleclick.net/gampad/ads?${paramsString}`;

    return adTagUrl;
  }
}

export default AdTag;
